/**
 * Seller AI Comp Analysis Component
 * Displays comp analysis for sellers using existing CMA report data
 */

import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Spinner, Table, Badge, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faHome,
  faDollarSign,
  faMapMarkerAlt,
  faCalendar,
  faRuler,
  faBed,
  faBath,
  faFileInvoiceDollar,
  faComments,
  faWandMagicSparkles,
  faLightbulb,
  faArrowUp,
  faArrowDown,
  faEquals,
  faCheckCircle,
  faExclamationTriangle,
  faBrain,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../../helpers/api';
import { toast } from 'react-hot-toast';

interface SellerAICompAnalysisProps {
  leadId: string;
  sellerProfile: any;
  propertyAddress?: string;
  onEditProfile?: () => void;
}

interface CompProperty {
  listing_id: string;
  address: string;
  close_price: number;
  close_date: string;
  living_area: number;
  bedrooms: number;
  bathrooms: number;
  year_built: number;
  distance_miles: number;
  price_per_sqft: number;
  comp_type?: 'renovated' | 'as-is';
}

const SellerAICompAnalysis: React.FC<SellerAICompAnalysisProps> = ({ 
  leadId, 
  sellerProfile, 
  propertyAddress,
  onEditProfile
}) => {
  const [loading, setLoading] = useState(true);
  const [cmaReport, setCmaReport] = useState<any>(null);
  const [comps, setComps] = useState<CompProperty[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    console.log('🏠 SellerAICompAnalysis mounted for lead:', leadId);
    console.log('📍 Property address:', propertyAddress);
    console.log('📊 Seller profile data:', sellerProfile);
    fetchOrCreateCMAReport();
  }, [leadId]);

  const fetchOrCreateCMAReport = async () => {
    try {
      setLoading(true);
      
      // First, try to fetch existing CMA reports for this lead
      const reportsResponse = await axiosInstance.get('/reports/my-reports', {
        params: {
          report_type: 'smart-cma',
          limit: 10
        }
      });

      console.log('📝 Reports API response:', reportsResponse.data);

      // Filter reports for this specific lead
      const allReports = reportsResponse.data.data || reportsResponse.data.reports || [];
      const leadReports = allReports.filter((report: any) => {
        // Check various possible field names for lead ID
        const reportLeadId = report.leadId || report.lead_id || report.userId || report.user_id;
        console.log(`🔍 Report ${report.id} - Lead ID field: ${reportLeadId} vs ${leadId}`);
        return reportLeadId === leadId;
      }) || [];

      if (leadReports.length > 0) {
        // Use the most recent report for this lead
        const report = leadReports[0];
        setCmaReport(report);
        
        // Parse comparables from the report
        if (report.cma_data?.comparableProperties) {
          const comparables = Array.isArray(report.cma_data.comparableProperties) 
            ? report.cma_data.comparableProperties 
            : [];
          setComps(comparables);
        }
        setLoading(false);
      } else {
        // No report exists - we'll show the generate UI
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching CMA report:', error);
      setLoading(false);
    }
  };

  const generateCMAReport = async () => {
    try {
      setGenerating(true);
      
      // Build property data from seller profile
      const constructAddress = () => {
        if (propertyAddress) return propertyAddress;
        
        // Try to construct from profile fields
        const parts = [];
        if (sellerProfile.property_address) parts.push(sellerProfile.property_address);
        if (sellerProfile.property_city) parts.push(sellerProfile.property_city);
        if (sellerProfile.property_state) parts.push(sellerProfile.property_state);
        if (sellerProfile.property_zip) parts.push(sellerProfile.property_zip);
        
        return parts.length > 0 ? parts.join(', ') : '';
      };

      const propertyData = {
        address: constructAddress(),
        bedrooms: sellerProfile.bedrooms || sellerProfile.property?.bedrooms,
        bathrooms: sellerProfile.bathrooms || sellerProfile.property?.bathrooms,
        yearBuilt: sellerProfile.year_built || sellerProfile.property?.yearBuilt,
        propertyType: sellerProfile.property_type || 'single_family',
        living_area: sellerProfile.square_footage || sellerProfile.property?.sqft,
        pool: sellerProfile.pool || false,
        garage_spaces: sellerProfile.garage_spaces || 0
      };

      // Open Smart CMA workflow to generate report
      const queryParams = new URLSearchParams({
        leadId: leadId,
        address: propertyData.address,
        bedrooms: propertyData.bedrooms?.toString() || '',
        bathrooms: propertyData.bathrooms?.toString() || '',
        sqft: propertyData.living_area?.toString() || '',
        yearBuilt: propertyData.yearBuilt?.toString() || '',
        step: '1'
      });

      window.open(`/smart-cma/workflow?${queryParams.toString()}`, '_blank');
      
      // Show message to user
      toast('Opening Smart CMA workflow. Please complete the report generation and then refresh this page.', { icon: 'ℹ️' });
      
      setGenerating(false);
      setLoading(false);
    } catch (error) {
      console.error('Error generating CMA report:', error);
      toast.error('Failed to generate comp analysis');
      setGenerating(false);
      setLoading(false);
    }
  };

  const getFilteredComps = () => {
    if (activeTab === 'renovated') {
      return comps.filter(c => c.comp_type === 'renovated' || c.address?.toLowerCase().includes('remodel'));
    } else if (activeTab === 'as-is') {
      return comps.filter(c => c.comp_type === 'as-is' || !c.address?.toLowerCase().includes('remodel'));
    }
    return comps;
  };

  const calculateStats = () => {
    const filteredComps = getFilteredComps();
    if (filteredComps.length === 0) return null;

    const prices = filteredComps.map(c => c.close_price);
    const ppsf = filteredComps.map(c => c.price_per_sqft);

    return {
      avgPrice: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
      medianPrice: Math.round(prices.sort((a, b) => a - b)[Math.floor(prices.length / 2)]),
      avgPPSF: Math.round(ppsf.reduce((a, b) => a + b, 0) / ppsf.length),
      count: filteredComps.length
    };
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading comp analysis...</span>
        </Spinner>
        <p className="mt-3">Loading comp analysis...</p>
      </div>
    );
  }

  if (generating) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Generating comp analysis...</span>
        </Spinner>
        <h5 className="mt-3">Generating Comp Analysis</h5>
        <p className="text-muted">Analyzing comparable properties in the area...</p>
      </div>
    );
  }

  // Helper function to get property address
  const getPropertyAddress = () => {
    if (propertyAddress) return propertyAddress;
    
    // Try to construct from profile fields
    const parts = [];
    if (sellerProfile?.property_address) parts.push(sellerProfile.property_address);
    if (sellerProfile?.property_city) parts.push(sellerProfile.property_city);
    if (sellerProfile?.property_state) parts.push(sellerProfile.property_state);
    if (sellerProfile?.property_zip) parts.push(sellerProfile.property_zip);
    
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const displayAddress = getPropertyAddress();

  if (!cmaReport && !generating) {
    // No CMA report exists yet - show option to generate
    return (
      <div className="seller-ai-comp-analysis">
        {/* Header */}
        <Alert variant="info" className="mb-4">
          <FontAwesomeIcon icon={faBrain} className="me-2" />
          <strong>AI-Powered Comp Analysis</strong> - Ready to analyze comparable properties
        </Alert>

        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <FontAwesomeIcon icon={faChartLine} size="3x" className="mb-3 text-primary" />
                <h5>No Comp Analysis Found</h5>
                <p className="text-muted mb-4">
                  Generate a comprehensive comparative market analysis to see how this property compares to recent sales in the area
                </p>
                
                {displayAddress ? (
                  <>
                    <div className="mb-4 p-3 bg-light rounded">
                      <FontAwesomeIcon icon={faHome} className="me-2" />
                      <strong>Property:</strong> {displayAddress}
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                      <Button 
                        variant="primary" 
                        size="lg"
                        onClick={generateCMAReport}
                      >
                        <FontAwesomeIcon icon={faChartLine} className="me-2" />
                        Run Comp Analysis
                      </Button>
                      {onEditProfile && (
                        <Button 
                          variant="outline-primary" 
                          size="lg"
                          onClick={onEditProfile}
                        >
                          <FontAwesomeIcon icon={faEdit} className="me-2" />
                          Edit Profile
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <Alert variant="warning">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                      No property address found. Please ensure the seller profile has a complete property address.
                    </Alert>
                    {onEditProfile && (
                      <Button 
                        variant="primary" 
                        size="lg"
                        onClick={onEditProfile}
                        className="mt-3"
                      >
                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                        Edit Seller Profile
                      </Button>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <div className="seller-ai-comp-analysis">
      {/* Header */}
      <Alert variant="info" className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <FontAwesomeIcon icon={faBrain} className="me-2" />
          <strong>AI-Powered Comp Analysis</strong> - {displayAddress || 'No address'}
        </div>
        <div className="d-flex gap-2">
          {onEditProfile && (
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={onEditProfile}
            >
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              Edit Profile
            </Button>
          )}
          {!cmaReport && (
            <Button 
              variant="primary" 
              size="sm"
              onClick={generateCMAReport}
              disabled={generating}
            >
              <FontAwesomeIcon icon={faChartLine} className="me-2" />
              Generate CMA
            </Button>
          )}
        </div>
      </Alert>

      {/* Action Cards */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <FontAwesomeIcon icon={faChartLine} size="3x" className="mb-3 text-primary" />
              <h5>Full CMA Report</h5>
              <p className="text-muted mb-4">
                View comprehensive market analysis with pricing recommendations
              </p>
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => {
                  window.open(`/smart-cma/workflow?reportId=${cmaReport?.id}&step=4`, '_blank');
                }}
                disabled={!cmaReport}
              >
                <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-2" />
                View Full Report
              </Button>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5">
              <FontAwesomeIcon icon={faWandMagicSparkles} size="3x" className="mb-3 text-info" />
              <h5>Interactive Chat</h5>
              <p className="text-muted mb-4">
                Ask questions about pricing, market trends, and comparable sales
              </p>
              <Button 
                variant="info" 
                size="lg"
                onClick={() => {
                  if (cmaReport?.id) {
                    window.open(`https://dev.chat.realestate/cma-report/${cmaReport.id}`, '_blank');
                  }
                }}
                disabled={!cmaReport}
              >
                <FontAwesomeIcon icon={faComments} className="me-2" />
                Open CMA Chat
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Comp Analysis Stats */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h6 className="text-muted mb-2">Total Comps</h6>
                <h3 className="mb-0">{stats.count}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h6 className="text-muted mb-2">Avg Sale Price</h6>
                <h3 className="mb-0">${stats.avgPrice.toLocaleString()}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h6 className="text-muted mb-2">Median Price</h6>
                <h3 className="mb-0">${stats.medianPrice.toLocaleString()}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h6 className="text-muted mb-2">Avg $/SqFt</h6>
                <h3 className="mb-0">${stats.avgPPSF}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Comparables Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white">
          <Nav variant="tabs" className="nav-tabs-modern">
            <Nav.Item>
              <Nav.Link 
                active={activeTab === 'all'} 
                onClick={() => setActiveTab('all')}
              >
                All Comps ({comps.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                active={activeTab === 'renovated'} 
                onClick={() => setActiveTab('renovated')}
              >
                Renovated ({comps.filter(c => c.comp_type === 'renovated').length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                active={activeTab === 'as-is'} 
                onClick={() => setActiveTab('as-is')}
              >
                As-Is ({comps.filter(c => c.comp_type === 'as-is').length})
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>
        <Card.Body className="p-0">
          {getFilteredComps().length > 0 ? (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>Sale Price</th>
                    <th>$/SqFt</th>
                    <th>Beds/Baths</th>
                    <th>SqFt</th>
                    <th>Year Built</th>
                    <th>Distance</th>
                    <th>Sale Date</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredComps().map((comp, idx) => (
                    <tr key={comp.listing_id || idx}>
                      <td>{comp.address}</td>
                      <td className="fw-bold">${comp.close_price?.toLocaleString()}</td>
                      <td>${comp.price_per_sqft}</td>
                      <td>{comp.bedrooms}/{comp.bathrooms}</td>
                      <td>{comp.living_area?.toLocaleString()}</td>
                      <td>{comp.year_built}</td>
                      <td>{comp.distance_miles?.toFixed(1)} mi</td>
                      <td>{new Date(comp.close_date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faHome} size="3x" className="text-muted mb-3" />
              <h5>No Comparable Properties Found</h5>
              <p className="text-muted">Try adjusting your search criteria or expanding the search radius</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SellerAICompAnalysis;